import { Barangay, BarangayTabulation, PollResponse, User, VoterTrackStats } from '../../types';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';

import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface BarangayListProps {}

const BarangayList: React.FC<BarangayListProps> = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [barangayGroups, setBarangayGroups] = useState<{[municipality: string]: Barangay[]}>({});
  const [finalReleaseCounts, setFinalReleaseCounts] = useState<{[barangay: string]: number}>({});
  const [exitPollsCounts, setExitPollsCounts] = useState<{[barangay: string]: number}>({});
  const [surveyPollsCounts, setSurveyPollsCounts] = useState<{[barangay: string]: number}>({});

  useEffect(() => {
    const fetchBarangays = async (): Promise<{[municipality: string]: Barangay[]} | null> => {
      try {
        const barangaysRef = collection(db, 'barangays');
        const q = query(barangaysRef, orderBy('city_municipality'), orderBy('barangay'));
        const querySnapshot = await getDocs(q);
        
        const groupedBarangays: {[municipality: string]: Barangay[]} = {};
        
        querySnapshot.forEach((doc) => {
          const barangay = { id: doc.id, ...doc.data() } as Barangay;
          if (!groupedBarangays[barangay.city_municipality]) {
            groupedBarangays[barangay.city_municipality] = [];
          }
          groupedBarangays[barangay.city_municipality].push(barangay);
        });
        
        setBarangayGroups(groupedBarangays);
        return groupedBarangays;
      } catch (error) {
        console.error('Error fetching barangays:', error);
      }
      return null;
    };

    const fetchLucillePolls = async (barangay: Barangay, user: User) => {
      try {
        const barangayTabulationPath =
          `tabulationsV2/${user.dashboard}/regions/${barangay.region}/`
          + `provinces/${barangay.province}/districts/${barangay.district}/`
          + `cities/${barangay.city_municipality}/barangays/${barangay.barangay}`;

        const barangayPollsRef = doc(db, barangayTabulationPath);
        const barangayPollsSnap = await getDoc(barangayPollsRef);
        
        if (barangayPollsSnap.exists()) {
          const barangayPollsData = barangayPollsSnap.data() as BarangayTabulation;
          const lucilleTally = barangayPollsData.tally['DISTRICT 1 REPRESENTATIVE']['Lucille Sering'] ?? 0;
          setSurveyPollsCounts((prevCounts) => ({ ...prevCounts, [barangay.id]: lucilleTally }));
        }
      } catch (error) {
        console.error('Error fetching lucille polls:', error);
      }
    };

    const fetchFinalReleases = async () => {
      try {
        const finalReleasesRef = doc(db, 'stats', 'finalReleases');
        const finalReleasesSnap = await getDoc(finalReleasesRef);
        
        if (finalReleasesSnap.exists()) {
          const finalReleasesData = finalReleasesSnap.data() as VoterTrackStats;
          setFinalReleaseCounts(finalReleasesData.barangays || {});
        }
      } catch (error) {
        console.error('Error fetching final releases:', error);
      }
    };

    const fetchExitPolls = async () => {
      try {
        const exitPollsRef = doc(db, 'stats', 'exitPolls');
        const exitPollsSnap = await getDoc(exitPollsRef);
        
        if (exitPollsSnap.exists()) {
          const exitPollsData = exitPollsSnap.data() as VoterTrackStats;
          setExitPollsCounts(exitPollsData.barangays || {});
        }
      } catch (error) {
        console.error('Error fetching exit polls:', error);
      }
    };

    if (user) {
      fetchBarangays().then((barangayGroups) => {
        if (barangayGroups) {
          for (const [municipality, barangays] of Object.entries(barangayGroups)) {
            console.log(`Processing barangays for ${municipality}`);
            for (const barangay of barangays) {
              fetchLucillePolls(barangay, user);
            }
          }
        }
      });
      fetchFinalReleases();
      fetchExitPolls();
    }
  }, [user]);

  const handleBarangayClick = (barangay: Barangay) => {
    navigate(`/barangay-voters?barangayName=${encodeURIComponent(barangay.barangay)}&municipality=${encodeURIComponent(barangay.city_municipality)}`);
  };

  return (
    <Box sx={{ width: '100%', typography: 'body1', p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Barangays by Municipality
      </Typography>
      <Grid container spacing={3}>
        {Object.entries(barangayGroups)
          .sort(([municipalityA], [municipalityB]) => municipalityA.localeCompare(municipalityB))
          .map(([municipality, barangays]) => (
            <Grid item xs={12} md={6} key={municipality}>
              <Card elevation={3}>
                <CardHeader 
                  title={`${municipality}, ${barangays[0].province}`}
                  titleTypographyProps={{ variant: 'h6' }}
                  subheader={`${barangays.length} Barangays`}
                />
                <CardContent>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Barangay</TableCell>
                          <TableCell align="right">Survey Polls</TableCell>
                          <TableCell align="right">Final Release</TableCell>
                          <TableCell align="right">Exit Polls</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {barangays.map((barangay) => (
                          <TableRow 
                            key={barangay.id} 
                            hover 
                            onClick={() => handleBarangayClick(barangay)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{barangay.barangay}</TableCell>
                            <TableCell align="right">
                              {surveyPollsCounts[barangay.id] ?? 'N/A'}
                            </TableCell>
                            <TableCell align="right">
                              {finalReleaseCounts[barangay.id] ?? 'N/A'}
                            </TableCell>
                            <TableCell align="right">
                              {exitPollsCounts[barangay.id] ?? 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
    </Box>
  );
}

export default BarangayList;
